import React from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { CreateProjectSchema, createProjectSchema } from "@/features/projects/schemas";
import { createProject } from "@/features/projects/services/project";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface CreateProjectFormProps {
  trigger?: React.ReactNode;
  tooltipClassName?: string;
  permissions: string[];
}

export default function CreateProjectForm(props: CreateProjectFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CreateProjectSchema>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, CreateProjectSchema>({
    mutationFn: createProject,
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PROJECTS], exact: true });
      navigate(`/projects/${id}`);
    },
  });

  async function onSubmit(values: CreateProjectSchema) {
    mutation.mutate(values);
  }

  return (
    <Dialog>
      <CreateProjectDialogTrigger trigger={props.trigger} tooltipClassName={props.tooltipClassName} permissions={props.permissions} />
      <DialogContent className="sm:max-w-lg">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>New project</DialogTitle>
              <DialogDescription>Create a new project to start tracking tickets and collaborating with your team.</DialogDescription>
            </DialogHeader>
            <FieldGroup className="py-4">
              {mutation.isError && <Error title="Unable to create project" error={mutation.error} />}
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Name</FieldLabel>
                    <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="e.g. Customer Portal" autoComplete="off" />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="description"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Description</FieldLabel>
                    <Textarea {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="What is this project about?" autoComplete="off" />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
            </FieldGroup>
            <DialogFooter showCloseButton>
              <Button type="submit" disabled={mutation.isPending || mutation.isSuccess}>
                <PlusIcon />
                {mutation.isPending || mutation.isSuccess ? "Creating..." : "Create project"}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

function CreateProjectDialogTrigger(props: CreateProjectFormProps) {
  const trigger = props.trigger || (
    <Button>
      <PlusIcon />
      New project
    </Button>
  );

  const allowed = props.permissions.includes(PERMISSIONS.PROJECT_CREATE);

  if (!allowed) {
    return (
      <PermissionTooltip allowed={false} message="You do not have permission to create projects." className={props.tooltipClassName}>
        {trigger as React.ReactElement}
      </PermissionTooltip>
    );
  }

  return <DialogTrigger asChild>{trigger}</DialogTrigger>;
}
